/**
 * Leviathan Web - Invoices Page
 * Quasar Prime LLC
 * Aggressive Tactical Industrial Design
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { GeometricPanel } from '@components/ui/GeometricPanel';
import { API_URL } from '@/config/api';

interface Invoice {
  id: string;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  status: string;
  total: number;
  amountPaid: number;
  amountDue: number;
  poNumber: string | null;
  customer: {
    id: string;
    name: string;
    customerNumber: string;
    email: string | null;
  };
  order: {
    id: string;
    orderNumber: string;
  } | null;
  payments: Array<{
    id: string;
    amount: number;
    paymentDate: string;
  }>;
}

interface Stats {
  total: number;
  draft: number;
  pending: number;
  sent: number;
  partial: number;
  overdue: number;
  paid: number;
  totalOutstanding: number;
  totalOverdue: number;
  receivedThisMonth: number;
}

interface StatCardProps {
  label: string;
  value: number | string;
  status?: 'operational' | 'warning' | 'critical';
}

function StatCard({ label, value, status }: StatCardProps) {
  const statusColor =
    status === 'operational'
      ? 'bg-success-green'
      : status === 'warning'
        ? 'bg-warning-amber'
        : status === 'critical'
          ? 'bg-error-red'
          : 'bg-steel';

  return (
    <div className="relative stat-card">
      <div className="absolute top-0 left-0 w-2 h-2 border-l border-t border-amber" />
      <div className="absolute top-0 right-0 w-2 h-2 border-r border-t border-amber" />
      <div className="absolute bottom-0 left-0 w-2 h-2 border-l border-b border-amber" />
      <div className="absolute bottom-0 right-0 w-2 h-2 border-r border-b border-amber" />

      <div className="bg-panel-dark border border-panel-border p-4 clip-path-octagon relative">
        {status && (
          <div className="absolute top-3 right-3">
            <div
              className={`w-2 h-2 ${statusColor} rounded-full animate-pulse`}
              style={{ boxShadow: '0 0 8px currentColor' }}
            />
          </div>
        )}
        <div className="text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
          {label}
        </div>
        <div
          className="text-2xl font-mono font-bold text-amber"
          style={{ textShadow: '0 0 10px rgba(255, 149, 0, 0.6)' }}
        >
          {value}
        </div>
      </div>
    </div>
  );
}

function getStatusColor(status: string): string {
  switch (status) {
    case 'DRAFT':
      return 'text-text-tertiary bg-text-tertiary/10';
    case 'PENDING':
      return 'text-steel bg-steel/10';
    case 'SENT':
      return 'text-amber bg-amber/10';
    case 'VIEWED':
      return 'text-amber bg-amber/10';
    case 'PARTIAL':
      return 'text-warning-amber bg-warning-amber/10';
    case 'PAID':
      return 'text-success-green bg-success-green/10';
    case 'OVERDUE':
      return 'text-error-red bg-error-red/10';
    case 'VOID':
      return 'text-text-tertiary bg-text-tertiary/10';
    case 'WRITTEN_OFF':
      return 'text-error-red bg-error-red/10';
    default:
      return 'text-text-secondary bg-text-secondary/10';
  }
}

const InvoicesPage = () => {
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showOverdue, setShowOverdue] = useState(false);

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    fetchInvoices();
  }, [search, statusFilter, showOverdue]);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/invoices/stats`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchInvoices = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      if (showOverdue) params.append('overdue', 'true');

      const response = await fetch(`${API_URL}/api/invoices?${params}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (response.ok) {
        const data = await response.json();
        setInvoices(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('');
    setShowOverdue(false);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatCurrency = (amount: number) => {
    return `$${Number(amount).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const isOverdue = (invoice: Invoice) => {
    if (['PAID', 'VOID', 'WRITTEN_OFF', 'DRAFT'].includes(invoice.status)) return false;
    return new Date(invoice.dueDate) < new Date();
  };

  const getDaysOverdue = (dueDate: string) => {
    const due = new Date(dueDate);
    const today = new Date();
    const diff = Math.floor((today.getTime() - due.getTime()) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : 0;
  };

  return (
    <div className="space-y-6 relative">
      {/* Header with geometric frame */}
      <div className="relative">
        <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber via-steel to-amber shadow-[0_0_8px_rgba(255,149,0,0.6)]" />
        <div className="border-l-4 border-amber pl-6 relative">
          <div className="flex items-center justify-between">
            <div>
              <h1
                className="text-4xl font-display font-bold text-steel tracking-wide uppercase"
                style={{ textShadow: '0 0 20px rgba(92, 140, 199, 0.6)' }}
              >
                Invoices
              </h1>
              <p className="text-text-secondary text-sm font-mono mt-1">
                Billing & Accounts Receivable
              </p>
            </div>
            <Link
              to="/invoices/new"
              className="bg-amber hover:bg-amber/90 text-background font-display font-bold py-3 px-6 transition-all uppercase tracking-wider relative group"
              style={{ boxShadow: '0 0 20px rgba(255, 149, 0, 0.4)' }}
            >
              <span className="relative z-10">+ New Invoice</span>
              <div
                className="absolute inset-0 bg-gradient-to-r from-amber to-steel opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ mixBlendMode: 'overlay' }}
              />
            </Link>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <StatCard label="Total" value={stats.total} />
          <StatCard label="Outstanding" value={formatCurrency(stats.totalOutstanding)} />
          <StatCard
            label="Overdue"
            value={formatCurrency(stats.totalOverdue)}
            status={stats.totalOverdue > 0 ? 'critical' : undefined}
          />
          <StatCard
            label="Overdue Count"
            value={stats.overdue}
            status={stats.overdue > 0 ? 'critical' : undefined}
          />
          <StatCard label="Paid" value={stats.paid} status="operational" />
          <StatCard label="Received (MTD)" value={formatCurrency(stats.receivedThisMonth)} />
        </div>
      )}

      {/* Filters Panel */}
      <GeometricPanel>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
              Search
            </label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Invoice #, PO, customer..."
              className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm transition-all placeholder:text-text-tertiary"
            />
          </div>
          <div>
            <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm transition-all"
            >
              <option value="">All Statuses</option>
              <option value="DRAFT">Draft</option>
              <option value="PENDING">Pending</option>
              <option value="SENT">Sent</option>
              <option value="PARTIAL">Partial</option>
              <option value="PAID">Paid</option>
              <option value="OVERDUE">Overdue</option>
              <option value="VOID">Void</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => setShowOverdue(!showOverdue)}
              className={`w-full font-mono py-2 px-4 transition-all uppercase text-xs tracking-wider border ${
                showOverdue
                  ? 'bg-error-red/20 text-error-red border-error-red/50'
                  : 'bg-panel-border hover:bg-steel/20 text-text-primary border-panel-border hover:border-steel'
              }`}
            >
              {showOverdue ? 'Showing Overdue' : 'Show Overdue'}
            </button>
          </div>
          <div className="flex items-end">
            <button
              onClick={clearFilters}
              className="w-full bg-panel-border hover:bg-steel/20 text-text-primary font-mono py-2 px-4 transition-all uppercase text-xs tracking-wider border border-panel-border hover:border-steel"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </GeometricPanel>

      {/* Invoices Table */}
      <div className="relative stat-card">
        <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
        <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
        <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
        <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

        <div className="bg-panel-dark border border-panel-border clip-path-octagon overflow-hidden">
          {isLoading ? (
            <div className="p-12 text-center">
              <div className="text-text-tertiary font-mono animate-pulse">
                &gt; Loading invoices...
              </div>
            </div>
          ) : invoices.length === 0 ? (
            <div className="p-12 text-center">
              <div className="text-text-tertiary font-mono text-lg mb-2">
                &gt; No invoices found
              </div>
              <p className="text-text-tertiary font-mono text-sm">
                {search || statusFilter || showOverdue
                  ? 'Try adjusting your search filters'
                  : 'Create an invoice from a delivered order'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-background border-b border-panel-border">
                  <tr>
                    <th className="text-left px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Invoice
                    </th>
                    <th className="text-left px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Customer
                    </th>
                    <th className="text-left px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Dates
                    </th>
                    <th className="text-right px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Total
                    </th>
                    <th className="text-right px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Balance
                    </th>
                    <th className="text-center px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Status
                    </th>
                    <th className="text-center px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((invoice) => {
                    const overdue = isOverdue(invoice);
                    const daysOver = getDaysOverdue(invoice.dueDate);

                    return (
                      <tr
                        key={invoice.id}
                        className={`border-b border-panel-border hover:bg-amber/5 transition-colors cursor-pointer ${overdue ? 'bg-error-red/5' : ''}`}
                        onClick={() => navigate(`/invoices/${invoice.id}`)}
                      >
                        <td className="px-6 py-4">
                          <div>
                            <div className="text-amber font-mono font-semibold">
                              {invoice.invoiceNumber}
                            </div>
                            {invoice.order && (
                              <div className="text-text-tertiary font-mono text-xs">
                                Order: {invoice.order.orderNumber}
                              </div>
                            )}
                            {invoice.poNumber && (
                              <div className="text-text-tertiary font-mono text-xs">
                                PO: {invoice.poNumber}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div>
                            <div className="text-text-primary font-mono text-sm">
                              {invoice.customer.name}
                            </div>
                            <div className="text-text-tertiary font-mono text-xs">
                              {invoice.customer.customerNumber}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-text-secondary font-mono text-sm">
                            {formatDate(invoice.invoiceDate)}
                          </div>
                          <div
                            className={`font-mono text-xs ${overdue ? 'text-error-red font-semibold' : 'text-text-tertiary'}`}
                          >
                            Due: {formatDate(invoice.dueDate)}
                            {overdue && ` (${daysOver}d overdue)`}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="text-text-primary font-mono font-semibold">
                            {formatCurrency(invoice.total)}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div
                            className={`font-mono font-semibold ${invoice.amountDue > 0 ? 'text-amber' : 'text-success-green'}`}
                            style={{
                              textShadow:
                                invoice.amountDue > 0
                                  ? '0 0 10px rgba(255, 149, 0, 0.4)'
                                  : undefined,
                            }}
                          >
                            {formatCurrency(invoice.amountDue)}
                          </div>
                          {invoice.amountPaid > 0 && invoice.amountDue > 0 && (
                            <div className="text-text-tertiary font-mono text-xs">
                              Paid: {formatCurrency(invoice.amountPaid)}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span
                            className={`${getStatusColor(overdue && !['PAID', 'VOID', 'WRITTEN_OFF'].includes(invoice.status) ? 'OVERDUE' : invoice.status)} font-mono text-xs uppercase tracking-wider font-semibold px-2 py-1 rounded`}
                          >
                            {overdue && !['PAID', 'VOID', 'WRITTEN_OFF'].includes(invoice.status)
                              ? 'OVERDUE'
                              : invoice.status}
                          </span>
                        </td>
                        <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-center space-x-2">
                            {invoice.amountDue > 0 && !['VOID', 'WRITTEN_OFF'].includes(invoice.status) && (
                              <button
                                onClick={() => navigate(`/invoices/${invoice.id}?action=payment`)}
                                className="text-success-green hover:text-success-green/80 font-mono text-sm"
                                title="Record Payment"
                              >
                                💵
                              </button>
                            )}
                            <button
                              onClick={() => navigate(`/invoices/${invoice.id}/edit`)}
                              className="text-text-secondary hover:text-amber font-mono text-sm"
                              title="Edit"
                            >
                              ✏️
                            </button>
                            <button
                              onClick={() => navigate(`/invoices/${invoice.id}`)}
                              className="text-steel hover:text-amber font-mono text-sm"
                              title="View"
                            >
                              👁️
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvoicesPage;
